import { useState, useEffect } from "react";
import { Template, TemplateCategory } from "@/types";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Search,
  Star,
  StarOff,
  Bot,
  Image as ImageIcon,
  Video,
  Code,
  BookOpen,
  Brain,
  GraduationCap,
  Crown,
  Lock,
  Sparkles,
  TrendingUp
} from "lucide-react";
import { cn } from "@/lib/utils";
import { TemplateDetailsDialog } from "@/components/TemplateDetailsDialog";

const categories: TemplateCategory[] = [
  {
    id: "chatbot",
    name: "Chatbot Agents",
    description: "Templates for creating AI chatbots and conversational agents",
    icon: "Bot",
    slug: "chatbot-agents"
  },
  {
    id: "image",
    name: "Image Prompts",
    description: "Prompts optimized for Midjourney, DALL·E, and other image AI",
    icon: "ImageIcon",
    slug: "image-prompts"
  },
  {
    id: "video",
    name: "Video Scripts",
    description: "Templates for video content creation and scripting",
    icon: "Video",
    slug: "video-scripts"
  },
  {
    id: "app",
    name: "App Development",
    description: "Prompts for software development and app creation",
    icon: "Code",
    slug: "app-development"
  },
  {
    id: "writing",
    name: "Research & Writing",
    description: "Templates for academic writing and research",
    icon: "BookOpen",
    slug: "research-writing"
  },
  {
    id: "brainstorm",
    name: "Brainstorming",
    description: "Prompts for ideation and creative thinking",
    icon: "Brain",
    slug: "brainstorming"
  },
  {
    id: "education",
    name: "Educational",
    description: "Templates for teaching and learning",
    icon: "GraduationCap",
    slug: "educational"
  }
];

const iconMap: Record<string, any> = {
  Bot,
  ImageIcon,
  Video,
  Code,
  BookOpen,
  Brain,
  GraduationCap
};

interface TemplatesTabProps {
  onSelectTemplate: (template: Template) => void;
}

export function TemplatesTab({ onSelectTemplate }: TemplatesTabProps) {
  const [activeCategory, setActiveCategory] = useState<string>("featured");
  const [searchQuery, setSearchQuery] = useState("");
  const [templates, setTemplates] = useState<Template[]>([]);
  const [favoriteTemplates, setFavoriteTemplates] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  useEffect(() => {
    loadTemplates();
    loadFavorites();
  }, [activeCategory]);

  const loadTemplates = async () => {
    setIsLoading(true);
    try {
      let query = supabase.from('templates').select('*');
      
      if (activeCategory !== 'featured') {
        query = query.eq('category', activeCategory);
      } else {
        query = query.order('usage_count', { ascending: false }).limit(6);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast({
        title: "Error",
        description: "Failed to load templates. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadFavorites = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data, error } = await supabase
        .from('user_template_interactions')
        .select('template_id')
        .eq('user_id', session.user.id)
        .eq('is_favorite', true);

      if (error) throw error;
      setFavoriteTemplates(new Set(data?.map(item => item.template_id)));
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  const toggleFavorite = async (templateId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Authentication required",
          description: "Please sign in to save favorites.",
          variant: "destructive",
        });
        return;
      }

      const isFavorite = favoriteTemplates.has(templateId);
      const newFavorites = new Set(favoriteTemplates);

      if (isFavorite) {
        newFavorites.delete(templateId);
      } else {
        newFavorites.add(templateId);
      }

      const { error } = await supabase
        .from('user_template_interactions')
        .upsert({
          user_id: session.user.id,
          template_id: templateId,
          is_favorite: !isFavorite,
          last_used: new Date().toISOString()
        });

      if (error) throw error;
      setFavoriteTemplates(newFavorites);

      toast({
        title: isFavorite ? "Removed from favorites" : "Added to favorites",
        description: `Template has been ${isFavorite ? 'removed from' : 'added to'} your favorites.`,
      });
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast({
        title: "Error",
        description: "Failed to update favorites. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    // Filter templates based on search query
    // This is done client-side for now, but could be moved to server-side for larger datasets
  };

  const filteredTemplates = templates.filter(template => {
    if (!searchQuery) return true;
    return (
      template.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  });

  const handleUseTemplate = async (template: Template) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // Track template usage
        await supabase
          .from('user_template_interactions')
          .upsert({
            user_id: session.user.id,
            template_id: template.id,
            last_used: new Date().toISOString(),
            usage_count: 1
          });

        // Increment template usage count
        await supabase
          .rpc('increment_template_usage', {
            template_id: template.id
          });
      }

      onSelectTemplate(template);

      toast({
        title: "Template loaded",
        description: "You can now edit and use the template.",
      });
    } catch (error) {
      console.error('Error using template:', error);
      toast({
        title: "Error",
        description: "Failed to load template. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleTemplateClick = (template: Template) => {
    setSelectedTemplate(template);
    setIsDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setIsDetailsOpen(false);
    setSelectedTemplate(null);
  };

  return (
    <div className="space-y-6">
      {/* Search Bar */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
          <Input
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Categories */}
      <Tabs value={activeCategory} onValueChange={setActiveCategory} className="w-full">
        <ScrollArea className="w-full">
          <TabsList className="inline-flex w-full min-w-max space-x-2 p-1">
            <TabsTrigger value="featured" className="flex items-center">
              <TrendingUp className="w-4 h-4 mr-2" />
              Featured
            </TabsTrigger>
            {categories.map((category) => {
              const Icon = iconMap[category.icon];
              return (
                <TabsTrigger
                  key={category.id}
                  value={category.id}
                  className="flex items-center"
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {category.name}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </ScrollArea>

        {/* Template Grid */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoading ? (
            // Loading skeletons
            Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader className="space-y-2">
                  <div className="h-4 w-3/4 bg-gray-200 rounded" />
                  <div className="h-3 w-1/2 bg-gray-200 rounded" />
                </CardHeader>
                <CardContent>
                  <div className="h-20 bg-gray-200 rounded" />
                </CardContent>
              </Card>
            ))
          ) : filteredTemplates.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <div className="text-gray-500">No templates found</div>
            </div>
          ) : (
            filteredTemplates.map((template) => (
              <Card 
                key={template.id} 
                className="group relative cursor-pointer hover:shadow-lg transition-all duration-200 transform hover:-translate-y-1"
                onClick={() => handleTemplateClick(template)}
              >
                {template.is_premium && (
                  <Badge className="absolute top-3 right-3 bg-gradient-to-r from-amber-500 to-yellow-500">
                    <Crown className="w-3 h-3 mr-1" />
                    Premium
                  </Badge>
                )}
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="text-lg">{template.title}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(template.id);
                      }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      {favoriteTemplates.has(template.id) ? (
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                      ) : (
                        <StarOff className="w-4 h-4" />
                      )}
                    </Button>
                  </CardTitle>
                  <CardDescription>{template.description}</CardDescription>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {template.tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-sm text-gray-600 line-clamp-3">
                    {template.template_text}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      Used {template.usage_count} times
                    </div>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUseTemplate(template);
                      }}
                      className={cn(
                        "flex items-center",
                        template.is_premium && "bg-gradient-to-r from-amber-500 to-yellow-500"
                      )}
                      disabled={template.is_premium}
                    >
                      {template.is_premium ? (
                        <>
                          <Lock className="w-4 h-4 mr-2" />
                          Premium
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Use Template
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </Tabs>

      {/* Template Details Dialog */}
      <TemplateDetailsDialog
        template={selectedTemplate}
        isOpen={isDetailsOpen}
        onClose={handleCloseDetails}
        onUse={onSelectTemplate}
        onToggleFavorite={toggleFavorite}
        isFavorite={selectedTemplate ? favoriteTemplates.has(selectedTemplate.id) : false}
      />
    </div>
  );
} 